import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { usePublicAuth } from '../context/PublicAuth';
import { myApplications, setPassword as apiSetPassword, formatApiErrorDetail } from '../lib/authApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, LogOut, Briefcase, ArrowRight, Sparkles, KeyRound } from 'lucide-react';
import Seo from '../components/Seo';

const PasswordSection = ({ user, refresh }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const hasPassword = !!user.has_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pass !== confirm) { setMsg({ ok: false, text: 'Passwords do not match' }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await apiSetPassword(pass, hasPassword ? current : undefined);
      setMsg({ ok: true, text: res.message });
      setCurrent(''); setPass(''); setConfirm('');
      refresh();
      setTimeout(() => setOpen(false), 1800);
    } catch (err) {
      setMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 rounded-2xl border p-5" data-testid="password-section">
      <div className="flex flex-wrap items-center gap-3">
        <KeyRound className="h-5 w-5 text-orange-500" />
        <div className="flex-1 min-w-0">
          <div className="font-bold">Password &amp; security</div>
          <div className="text-xs text-slate-500">
            {hasPassword ? 'Email & password sign-in is enabled for your account.' : 'Set a password to also sign in with email & password.'}
          </div>
        </div>
        <Button data-testid="toggle-password-form-btn" variant="outline" onClick={() => { setOpen(v => !v); setMsg(null); }}>
          {open ? 'Close' : hasPassword ? 'Change password' : 'Set password'}
        </Button>
      </div>
      {open && (
        <form onSubmit={onSubmit} className="mt-5 grid gap-3 max-w-md" data-testid="set-password-form">
          {hasPassword && (
            <Input data-testid="current-password-input" type="password" value={current} onChange={e => setCurrent(e.target.value)} placeholder="Current password" required />
          )}
          <Input data-testid="new-password-input" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="New password (min 8 characters)" required minLength={8} />
          <Input data-testid="confirm-password-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm new password" required minLength={8} />
          {msg && (
            <div data-testid="password-msg" className={`text-sm px-3 py-2 rounded-lg border ${msg.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>{msg.text}</div>
          )}
          <Button data-testid="save-password-btn" type="submit" disabled={busy} className="bg-orange-500 hover:bg-orange-600 w-fit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save password'}
          </Button>
        </form>
      )}
    </div>
  );
};

const scoreColor = (s) => {
  if (s === undefined || s === null) return 'bg-slate-300 text-slate-700';
  if (s >= 80) return 'bg-emerald-500 text-white';
  if (s >= 60) return 'bg-amber-500 text-white';
  return 'bg-rose-500 text-white';
};

const STAGE_LABEL = {
  applied: 'Applied', screening: 'Screening', shortlisted: 'Shortlisted',
  interview_scheduled: 'Interview Scheduled', interview_completed: 'Interview Done',
  selected: 'Selected', offer_sent: 'Offer Sent', offer_accepted: 'Offer Accepted',
  offer_rejected: 'Offer Rejected', joined: 'Joined', rejected: 'Not Selected',
};

export default function AccountPage() {
  const { user, loading, logout, refresh } = usePublicAuth();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login', { replace: true }); return; }
    myApplications().then(setApps).finally(() => setAppsLoading(false));
  }, [user, loading, nav]);

  if (loading || !user) return (
    <div className="min-h-screen bg-white"><Navbar /><div className="pt-40 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo title="Your Account | Hipro Technologies" description="Manage your Hipro Technologies account and job applications." path="/account" noindex />
      <Navbar />

      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 flex flex-wrap items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-16 w-16 rounded-full ring-2 ring-orange-400" data-testid="account-avatar" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-orange-500 flex items-center justify-center text-2xl font-bold">{(user.name || user.email || '?')[0].toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-orange-400">Your account</div>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold" data-testid="account-name">{user.name || user.email}</h1>
            <div className="text-slate-300 text-sm" data-testid="account-email">{user.email}</div>
          </div>
          <div className="ml-auto flex gap-2">
            <Link to="/careers" className="inline-flex items-center gap-1 rounded-lg bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-semibold">Browse jobs <ArrowRight className="h-4 w-4" /></Link>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav('/'); }} className="inline-flex items-center gap-1 rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/10"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="text-lg font-bold flex items-center gap-2"><Briefcase className="h-5 w-5 text-orange-500" /> Your applications</h2>

          {appsLoading ? (
            <div className="mt-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : apps.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed py-16 text-center text-slate-500" data-testid="no-applications">
              You haven't applied to any roles yet.
              <div className="mt-4"><Button onClick={() => nav('/careers')} className="bg-orange-500 hover:bg-orange-600">See open roles</Button></div>
            </div>
          ) : (
            <div className="mt-4 grid gap-3" data-testid="applications-list">
              {apps.map(a => (
                <div key={a.id} className="rounded-2xl border p-5 flex flex-wrap items-center gap-4" data-testid={`application-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500 font-mono">{a.candidate_id} · {a.job_id}</div>
                    <div className="mt-1 font-bold text-slate-900">{a.job_title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Applied {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  {a.ai_score?.score !== undefined && (
                    <div className={`rounded-full text-xs font-bold px-2.5 py-1 ${scoreColor(a.ai_score.score)}`} title={a.ai_score.summary || ''}>
                      <Sparkles className="h-3 w-3 inline -mt-0.5 mr-1" /> AI {a.ai_score.score}/100
                    </div>
                  )}
                  <span className="text-xs bg-slate-100 border rounded-full px-2.5 py-1 font-semibold text-slate-700">{STAGE_LABEL[a.stage] || a.stage}</span>
                </div>
              ))}
            </div>
          )}

          <PasswordSection user={user} refresh={refresh} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
